import AiAgentChat, { AiAgentChatRef } from 'components/AiAgentChat';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { FC, useRef, useState } from 'react';
import ProposalCard, { ProposalWithTransactions } from './ProposalCard';

type Props = {
  vehicleId: string;
};

const AiAgent: FC<Props> = ({ vehicleId }) => {
  const chatRef = useRef<AiAgentChatRef>(null);
  const [uploadedInvoiceFile, setUploadedInvoiceFile] = useState<{
    path: string;
    name: string;
  } | null>(null);
  const utils = api.useUtils();

  const chatMutation = api.aiAgent.chatIncomeExpense.useMutation(
    useHandleTrpcMutation()({
      invalidations: [],
    }),
  );

  const executeMutation = api.aiAgent.executeIncomeExpense.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Income/expense created successfully',
      onSuccess: () => {
        utils.incomeAndExpenses.list.invalidate();
        setUploadedInvoiceFile(null);
        chatRef.current?.addMessage({
          role: 'assistant',
          content:
            'The income/expense has been created successfully! You can see it in the list below.',
        });
      },
    }),
  );

  const handleApproveProposal = (proposal: ProposalWithTransactions) => {
    const { transactions, fxRate, ...rest } = proposal;
    executeMutation.mutate({
      vehicleId,
      proposal: rest,
      fxRate: fxRate ?? undefined,
      invoiceFile: uploadedInvoiceFile ?? undefined,
      transactions: transactions?.map((t) => ({
        transactionTypeId: t.transactionTypeId,
        operationType: t.operationType,
        amountExVatTcy: t.amountExVatTcy,
        amountIncVatTcy: t.amountIncVatTcy,
        transactionCurrency: t.transactionCurrency,
        transactionSubType: t.transactionSubType,
      })),
    });
  };

  return (
    <AiAgentChat
      ref={chatRef}
      vehicleId={vehicleId}
      description="Upload an invoice or describe an income/expense to create"
      inputPlaceholder="Describe the income or expense you want to create..."
      isExecuting={executeMutation.isPending}
      onChat={(params) => chatMutation.mutateAsync(params)}
      onApproveProposal={handleApproveProposal}
      onFileUploaded={(result) => setUploadedInvoiceFile(result)}
      renderProposalCard={(props) => (
        <ProposalCard
          proposal={props.proposal}
          vehicleId={props.vehicleId}
          onApprove={props.onApprove}
          onReject={props.onReject}
          isExecuting={props.isExecuting}
        />
      )}
    />
  );
};

export default AiAgent;
